import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sun, Moon, ChevronDown } from 'lucide-react';
import Logo from '../assets/logo';
import { useTheme } from '../context/ThemeContext';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  // Track scroll position for navbar styling
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Only show Login if not disabled via env
  // Use Vite's built-in MODE for reliability
  const isProd = import.meta.env.MODE === 'production';
  const disableLogin = isProd;

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/research', label: 'Research' },
    {
      href: `/navigator?theme=${theme}`,
      label: 'Navigator',
      external: true,
    },
    { to: '/services', label: 'Services' },
    { to: '/blog', label: 'Insights' },
    { to: '/about', label: 'About' },
    { to: '/contact', label: 'Contact' },
    // Conditionally include Login ONLY if not in production
    ...(!disableLogin ? [{ to: '/admin', label: 'Login' }] : [])
  ];

  const isPathActive = (path) =>
    location.pathname === path || (path !== '/' && location.pathname.startsWith(path));

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <nav className={`${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-white text-slate-800 border-b border-slate-200'} ${scrolled ? 'shadow-md' : 'shadow-soft'} sticky top-0 z-50 transition-shadow duration-300`}>
      <div className="w-full px-5 lg:px-10 py-3 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-3 shrink-0" aria-label="Shared Oxygen Home">
          <Logo
            width="88px"
            height="88px"
            maxWidth="88px"
            className="logo-header"
            alt="Shared Oxygen Logo"
          />
          <span className="flex flex-col leading-tight">
            <span className={`text-base lg:text-lg font-bold tracking-tight whitespace-nowrap ${theme === 'dark' ? 'text-white' : 'text-slate-950'}`}>
              Shared Oxygen, LLC
            </span>
            <span className={`text-[11px] lg:text-xs font-semibold tracking-[0.16em] uppercase whitespace-nowrap ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
              AI &amp; Data Advisory
            </span>
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-8">
          {navLinks.map((link) => {
            const destination = link.to || link.href || link.label;
            const isActive = Boolean(link.to) && isPathActive(link.to);

            if (link.children) {
              const groupActive =
                (link.to && isPathActive(link.to)) ||
                link.children.some((child) => isPathActive(child.to));
              const triggerClass = `relative py-2 inline-flex items-center gap-1 hover:text-blue-700 transition-colors font-medium ${
                groupActive
                  ? 'text-blue-700 font-semibold'
                  : theme === 'dark' ? 'text-gray-200' : 'text-slate-800'
              }`;

              return (
                <div key={link.label} className="relative group">
                  {link.to ? (
                    <Link
                      to={link.to}
                      className={triggerClass}
                      aria-haspopup="true"
                      onClick={() => window.scrollTo(0, 0)}
                    >
                      {link.label}
                      <ChevronDown size={16} className="opacity-70 group-hover:rotate-180 transition-transform" />
                      {groupActive && (
                        <span className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 rounded-full" />
                      )}
                    </Link>
                  ) : (
                    <button type="button" className={triggerClass} aria-haspopup="true">
                      {link.label}
                      <ChevronDown size={16} className="opacity-70 group-hover:rotate-180 transition-transform" />
                      {groupActive && (
                        <span className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 rounded-full" />
                      )}
                    </button>
                  )}
                  <div
                    className={`absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all ${
                      theme === 'dark' ? '' : ''
                    }`}
                  >
                    <div
                      className={`min-w-[11rem] py-2 rounded-lg shadow-lg border ${
                        theme === 'dark'
                          ? 'bg-gray-800 border-gray-700'
                          : 'bg-white border-slate-300'
                      }`}
                    >
                      {link.children.map((child) => {
                        const childActive = isPathActive(child.to);
                        return (
                          <Link
                            key={child.to}
                            to={child.to}
                            className={`block px-4 py-2 text-sm font-medium transition-colors ${
                              childActive
                                ? 'text-blue-700 bg-blue-50 dark:bg-blue-900/40'
                                : theme === 'dark'
                                  ? 'text-gray-200 hover:text-blue-400 hover:bg-gray-700/60'
                                  : 'text-slate-800 hover:text-blue-700 hover:bg-slate-50'
                            }`}
                            onClick={() => window.scrollTo(0, 0)}
                          >
                            {child.label}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            }

            if (link.external) {
              return (
                <a
                  key={destination}
                  href={link.href}
                  className={`relative py-2 hover:text-blue-700 transition-colors font-medium ${
                    theme === 'dark' ? 'text-gray-200' : 'text-slate-800'
                  }`}
                  onClick={() => window.scrollTo(0, 0)}
                >
                  {link.label}
                </a>
              );
            }

            return (
              <Link
                key={destination}
                to={link.to}
                className={`relative py-2 hover:text-blue-700 transition-colors font-medium ${isActive
                  ? 'text-blue-700 font-semibold'
                  : theme === 'dark' ? 'text-gray-200' : 'text-slate-800'
                  }`}
                onClick={() => window.scrollTo(0, 0)}
              >
                {link.label}
                {isActive && (
                  <span className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-700 rounded-full" />
                )}
              </Link>
            );
          })}

          {/* Theme Toggle Button */}
          <button
            onClick={toggleTheme}
            className={`p-2 rounded-full ${theme === 'dark' ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' : 'bg-slate-200 text-slate-800 hover:bg-slate-300'} transition-colors`}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>

        {/* Mobile Menu Toggle and Theme Button */}
        <div className="md:hidden flex items-center space-x-2">
          <button
            onClick={toggleTheme}
            className={`p-2 rounded-full ${theme === 'dark' ? 'bg-gray-800 text-yellow-400' : 'bg-slate-200 text-slate-800'} transition-colors`}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          <button
            onClick={toggleMenu}
            className={`${theme === 'dark' ? 'text-gray-100 hover:text-white hover:bg-gray-800' : 'text-slate-700 hover:text-slate-950 hover:bg-slate-100'} focus:outline-none p-2 rounded-md transition-colors`}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        {isMenuOpen && (
          <div className="md:hidden fixed inset-0 bg-black/50 dark:bg-black/70 z-40" onClick={toggleMenu}>
            <div
              className={`fixed top-0 right-0 w-72 h-full shadow-lg p-6 animate-slide-left ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-white text-slate-800 border-l border-slate-200'}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-8">
                <Link to="/" className="flex items-center gap-3" aria-label="Shared Oxygen Home" onClick={toggleMenu}>
                  <Logo
                    width="68px"
                    height="68px"
                    maxWidth="68px"
                    className="logo-mobile"
                    alt="Shared Oxygen Logo"
                  />
                  <span className="flex flex-col leading-tight">
                    <span className={`text-base font-bold whitespace-nowrap ${theme === 'dark' ? 'text-white' : 'text-slate-950'}`}>
                      Shared Oxygen, LLC
                    </span>
                    <span className={`text-[11px] font-semibold tracking-[0.14em] uppercase whitespace-nowrap ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                      AI &amp; Data Advisory
                    </span>
                  </span>
                </Link>
                <button
                  onClick={toggleMenu}
                  className={`${theme === 'dark' ? 'text-gray-300 hover:text-white hover:bg-gray-800' : 'text-slate-700 hover:text-slate-950 hover:bg-slate-100'} p-2 rounded-md transition-colors`}
                  aria-label="Close menu"
                >
                  <X size={22} />
                </button>
              </div>
              <div className="space-y-4">
                {navLinks.map((link) => {
                  const destination = link.to || link.href || link.label;
                  const isActive = Boolean(link.to) && isPathActive(link.to);

                  if (link.children) {
                    const groupActive =
                      (link.to && isPathActive(link.to)) ||
                      link.children.some((child) => isPathActive(child.to));
                    const sectionLabelClass = `px-2 py-2 text-xs font-semibold uppercase tracking-[0.2em] ${
                      groupActive
                        ? 'text-blue-700'
                        : theme === 'dark'
                          ? 'text-slate-400'
                          : 'text-slate-600'
                    }`;

                    return (
                      <div key={link.label}>
                        {link.to ? (
                          <Link
                            to={link.to}
                            onClick={() => {
                              toggleMenu();
                              window.scrollTo(0, 0);
                            }}
                            className={sectionLabelClass}
                          >
                            {link.label}
                          </Link>
                        ) : (
                          <p className={sectionLabelClass}>{link.label}</p>
                        )}
                        <div className="space-y-1 pl-3">
                          {link.children.map((child) => {
                            const childActive = isPathActive(child.to);
                            return (
                              <Link
                                key={child.to}
                                to={child.to}
                                onClick={() => {
                                  toggleMenu();
                                  window.scrollTo(0, 0);
                                }}
                                className={`block px-2 py-3 transition-colors rounded-md ${
                                  childActive
                                    ? 'text-blue-700 font-semibold bg-blue-50 dark:bg-blue-900/40'
                                    : theme === 'dark'
                                      ? 'text-gray-200 hover:text-blue-400'
                                      : 'text-slate-800 hover:text-blue-700'
                                }`}
                              >
                                {child.label}
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }

                  if (link.external) {
                    return (
                      <a
                        key={destination}
                        href={link.href}
                        onClick={() => {
                          toggleMenu();
                          window.scrollTo(0, 0);
                        }}
                        className={`block px-2 py-3 transition-colors ${
                          theme === 'dark' ? 'text-gray-200 hover:text-blue-400' : 'text-slate-800 hover:text-blue-700'
                        }`}
                      >
                        {link.label}
                      </a>
                    );
                  }

                  return (
                    <Link
                      key={destination}
                      to={link.to}
                      onClick={() => {
                        toggleMenu();
                        window.scrollTo(0, 0);
                      }}
                      className={`block px-2 py-3 transition-colors ${isActive
                        ? 'text-blue-700 font-semibold bg-blue-50 dark:bg-blue-900/40 rounded-md'
                        : `${theme === 'dark' ? 'text-gray-200 hover:text-blue-400' : 'text-slate-800 hover:text-blue-700'}`
                        }`}
                    >
                      {link.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
